'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import { toast } from 'sonner';
import * as THREE from 'three';

import { drainQueuedExternalRefresh } from '../project-file-watch/externalRefresh';
import { refreshAfterBuild } from '../project-file-watch/postBuildRefresh';
import { shouldOpenProjectBeforeStartup } from '../projectLifecycleHelpers';
import { saveScene } from '../sceneHandler';

import { saveMaterialAsset, saveResourceAsset } from './assetActions';
import { multiplayerServerManager } from './multiplayerServerManager';
import { savePrefabAsset } from './prefabActions';

import type { EditorResourceData } from './assetActions';
import type { BlenderCheckResult, LagSimulationConfig, PnpmCheckResult, ToolCallingResult } from '@/app/sdk';
import type { PlayLaunchMode } from '@/editor/core/play-preview/playPreviewTypes';
import type { FileServerProvider } from '@/lib/storage/file-server';

import { globalStore } from '@/app/globalStore';
import { sdk } from '@/app/sdk';
import { DEFAULT_FILE_SERVER_PORT, DEFAULT_RENDERER_TYPE, USE_ISOLATED_PLAY_MODE } from '@/const';
import { type IAssetFile } from '@/editor/core/assetHandlers/assetHandler';
import { AssetType } from '@/editor/core/assetTypes';
import { getPlayModeSceneDisplayName } from '@/editor/core/play-preview/playModeSceneDisplayName';
import { PlayLaunchMode as PlayLaunchModeValues } from '@/editor/core/play-preview/playPreviewTypes';
import { project, sceneStore, store } from '@/editor/store';
import { PlayModeSceneType } from '@/editor/store/editorStore';
import { storageFactory, StorageProviderType } from '@/lib/storage';
import { syncFileServerProviderUrlFromSdk } from '@/lib/storage/file-server';
import { IS_DEV } from '@/lib/utils';

const DEBUG_LOG = {
  notifyChanged: false && IS_DEV,
  editorSave: true,
  performance: true,
  openProject: true,
  buildProject: true,
  reloadWorld: true,
} as const;

function createLagSimulationConfig(
  enabled: boolean,
  latencyMs: number,
  jitterMs: number,
  packetLoss: number
): LagSimulationConfig | undefined {
  if (!enabled) {
    return undefined;
  }
  return {
    enabled: true,
    minDelay: Math.max(0, latencyMs - jitterMs),
    maxDelay: latencyMs + jitterMs,
    packetLoss,
  };
}

/** Max wait for isolated SDK play view/window start (`sdk.playMode.start` / `startWindow`). */
const PLAY_MODE_START_TIMEOUT_MS = 25_000;

/** Tear down all SDK play-mode surfaces (embedded view, primary window, secondary windows). */
async function stopSdkPlayModeResources(): Promise<void> {
  if (!sdk) {
    return;
  }
  await sdk.playMode.stop();
  await sdk.playMode.stopWindow();
  await sdk.playMode.stopSecondaryWindow();
}
/** Max wait for editor world/scene to be ready before save + build at PIE entry. */
const PLAY_MODE_START_SCENE_READY_TIMEOUT_MS = 20_000;
/** Poll interval for {@link waitForSceneReadyForSave} (not a timeout itself). */
const SCENE_READY_POLL_INTERVAL_MS = 100;
/** Max wait for in-flight save/build to finish before external project open/switch. */
const EXTERNAL_PROJECT_SWITCH_IO_IDLE_TIMEOUT_MS = 20_000;
/** Poll interval for {@link waitForProjectIoIdle} (not a timeout itself). */
const EXTERNAL_PROJECT_SWITCH_IO_IDLE_POLL_MS = 100;

export type ExternalProjectOpenResult = {
  success: boolean;
  errorMessage?: string;
};

function isSceneReadyForSave(): boolean {
  return Boolean(
    store.project.currentScene
    && sceneStore.world
    && sceneStore.scene
    && !sceneStore.isCreating
    && !sceneStore.isResettingWorld
  );
}

async function waitForSceneReadyForSave(timeoutMs: number = PLAY_MODE_START_SCENE_READY_TIMEOUT_MS): Promise<boolean> {
  if (isSceneReadyForSave()) {
    return true;
  }

  const start = performance.now();
  while ((performance.now() - start) < timeoutMs) {
    await new Promise(resolve => setTimeout(resolve, SCENE_READY_POLL_INTERVAL_MS));
    if (isSceneReadyForSave()) {
      return true;
    }
  }

  return false;
}

async function waitForProjectIoIdle(timeoutMs: number = EXTERNAL_PROJECT_SWITCH_IO_IDLE_TIMEOUT_MS): Promise<boolean> {
  const startTime = Date.now();
  while (store.isSaving || store.isBuildingProject) {
    if (Date.now() - startTime >= timeoutMs) {
      return false;
    }
    await new Promise(resolve => setTimeout(resolve, EXTERNAL_PROJECT_SWITCH_IO_IDLE_POLL_MS));
  }
  return true;
}

function normalizeProjectPathForCompare(projectFolder: string, projectFile: string): string {
  return `${projectFolder.replace(/\\/g, '/').toLowerCase()}::${projectFile.toLowerCase()}`;
}

interface SetPlayModeActiveOptions {
  launchMode?: PlayLaunchMode;
}

function getPlayModeLoadingMessage(
  launchMode: PlayLaunchMode,
  playModeSceneLabel: string,
): string {
  switch (launchMode) {
    case PlayLaunchModeValues.NewWindow:
      return `Starting Play in New Window (${playModeSceneLabel})...`;
    case PlayLaunchModeValues.MobilePreview:
      return `Starting Mobile Preview (${playModeSceneLabel})...`;
    case PlayLaunchModeValues.Embedded:
    default:
      return `Starting Play Mode (${playModeSceneLabel})...`;
  }
}

function makePlayWindowConfig(launchMode: PlayLaunchMode, scenePath: string | null) {
  const projectName = project.projectName?.trim() || 'Project';
  const modeSuffix = launchMode === PlayLaunchModeValues.MobilePreview
    ? 'Mobile Preview'
    : '';
  const sceneName = getPlayModeSceneDisplayName(scenePath);
  const titleParts = [projectName];
  if (sceneName) titleParts.push(sceneName);
  if (modeSuffix) titleParts.push(modeSuffix);
  const title = titleParts.join(' - ');
  return {
    width: store.playWindowWidth,
    height: store.playWindowHeight,
    devicePixelRatio: store.playWindowDevicePixelRatio,
    title,
    center: store.playWindowCenter,
    resizable: store.playWindowResizable,
  };
}

function makePlayMobilePreviewConfig() {
  return {
    presetId: store.playPreviewPresetId,
    orientation: store.playPreviewOrientation,
    width: store.playPreviewWidth,
    height: store.playPreviewHeight,
    devicePixelRatio: store.playPreviewDevicePixelRatio,
    userAgent: store.playPreviewUserAgent,
    platform: store.playPreviewPlatform,
    mobileViewport: store.playPreviewMobileViewport,
    touchEmulation: store.playPreviewTouchEmulation,
    showDeviceFrame: store.playPreviewShowDeviceFrame,
    showNotchOverlay: store.playPreviewShowNotchOverlay,
    safeAreaInsets: store.playPreviewSafeAreaInsets,
  };
}

function safeJsonForLog(value: unknown): string {
  try {
    if (value === null || value === undefined) return String(value);
    return JSON.stringify(value);
  } catch {
    return '[non-JSON-serializable value]';
  }
}

/**
 * Notify the editor that a change has occurred.
 * @param action - The action that occurred.
 * @param value - The value of the action.
 */
export function notifyChanged(action: string, value: any) { // TODO: Type safe value?
  // if (action === 'Value' && getType(value.asset) === AssetFile) {
  //   value.asset.save(); // Auto save the asset if it is an AssetFile
  // }
  if (DEBUG_LOG.notifyChanged) {
    console.log('[NotifyChanged]', action, safeJsonForLog(value));
    // TODO: Auto save/ history
  }
}

export { reloadCurrentWorld } from '../project-file-watch/reloadWorld';
export { refreshAfterBuild };

/**
 * Build the current project.
 * @returns True if the project was built successfully, false otherwise.
 */
export async function buildProject(options: { reloadWorldOnSuccess?: boolean } = {}): Promise<boolean> {
  const { reloadWorldOnSuccess = true } = options;

  if (store.isBuildingProject) {
    console.trace('[EditorActions] Build already in progress');
    return false;
  }

  const buildStart = performance.now();
  store.setIsBuildingProject(true);
  globalStore.showLoadingOverlay('buildProject', { message: 'Building Project...', pinned: true });
  let success = false;
  let result: ToolCallingResult | undefined = undefined;
  if (sdk) {
    result = await sdk.tools.buildProject(project.projectPath);
    success = result.success;
  } else {
    const storageProvider = store.project.makeScopedStorageProvider();
    success = await storageProvider.buildCurrentProject();
  }

  if (success) {
    console.log('🔨 [buildProject] Build successful, refreshing to pick up new metadata...');
    await refreshAfterBuild({ reloadWorld: reloadWorldOnSuccess });
    globalStore.setBuildErrors([]); // Clear any previous build errors
    globalStore.clearPlayModeErrors();
    toast.info(result?.message ?? 'Build successful');
  } else {
    globalStore.setBuildErrors([result?.message ?? 'Build failed']);
    globalStore.setShowBuildErrorOverlay(true);
  }

  globalStore.hideLoadingOverlay('buildProject');
  store.setIsBuildingProject(false);
  void drainQueuedExternalRefresh();

  if (DEBUG_LOG.buildProject) console.log('[EditorActions] 🔨 Build project took:', ((performance.now() - buildStart) / 1000).toFixed(2), 's');
  return success;
}

/**
 * Open the upload project dialog.
 */
export function uploadProject() {
  store.setUploadProjectDialogOpen(true);
}

/**
 * Save the current scene.
 * @param showSuccess - Whether to show a success toast.
 * @param showError - Whether to show an error toast.
 */
export async function editorSave(showSuccess: boolean = false, showError: boolean = true): Promise<boolean> {
  if (globalStore.hasRenderError) {
    console.warn('[EditorActions] Cannot save while editor has render error.');
    toast.error('Cannot save: Editor has encountered an error');
    return false;
  }

  if (store.isSaving) {
    console.trace('[EditorActions] Save already in progress');
    return false;
  }

  if (!isSceneReadyForSave()) {
    console.warn('[EditorActions] Cannot save while scene is not fully loaded yet.', {
      currentScene: store.project.currentScene,
      isCreating: sceneStore.isCreating,
      isResettingWorld: sceneStore.isResettingWorld,
      hasWorld: Boolean(sceneStore.world),
      hasScene: Boolean(sceneStore.scene),
    });
    showError && toast.info('Scene is still loading, please wait before saving.');
    return false;
  }

  store.setIsSaving(true);
  const minDisplayTime = 500;
  const logPerformance = (label: string, start: number) => { if (DEBUG_LOG.performance) console.log(`[EditorActions] 💾 Save: ${label} took ${((performance.now() - start) / 1000).toFixed(2)}s`); };
  const saveTimeStart = performance.now();

  try {
    // Save current scene
    let start = performance.now();
    await saveScene();
    logPerformance('saveScene', start);

    // Also save the current editing prefab actor if there is one
    if (store.isPrefabMode && store.selectedObject?.asActor) {
      start = performance.now();
      await savePrefabAsset(store.selectedObject.asActor, true); // silent = true to avoid duplicate toast
      logPerformance('savePrefabAsset', start);
    }

    // Also save the current editing material asset if there is one
    const propertyStore = store.inspectorPanel.propertyStore;
    if (propertyStore && store.selectedAssetItem?.assetType === AssetType.Material) {
      const materialTarget = propertyStore.targets.find((target): target is THREE.Material =>
        target instanceof THREE.Material
      );
      if (materialTarget) {
        start = performance.now();
        await saveMaterialAsset(store.selectedAssetItem as IAssetFile, materialTarget, {
          silent: true,
          reloadDependents: true,
        }); // silent = true to avoid duplicate toast
        logPerformance('saveMaterialAsset', start);
      }
    }

    // Also save the current editing resource asset if there is one
    if (propertyStore && (store.selectedAssetItem?.assetType === AssetType.Resource)) {
      const resourceTarget = propertyStore.targets.find((target): target is EditorResourceData =>
        target != null && typeof target === 'object' && 'class' in target && 'instance' in target
      );
      if (resourceTarget) {
        start = performance.now();
        await saveResourceAsset(store.selectedAssetItem as IAssetFile, resourceTarget, true); // silent = true to avoid duplicate toast
        logPerformance('saveResourceAsset', start);
      }
    }

    if (DEBUG_LOG.editorSave || DEBUG_LOG.performance) {
      console.log('[EditorActions] 💾 Save Total time:', ((performance.now() - saveTimeStart) / 1000).toFixed(2), 's');
    }
    showSuccess && toast.success('Save Success');
    return true;
  } catch (error) {
    console.error('Failed to save scene:', error);
    showError && toast.error('Save Failed');
    return false;
  } finally {
    const elapsed = performance.now() - saveTimeStart;
    if (elapsed < minDisplayTime) await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
    store.setIsSaving(false);
  }
}

/**
 * Verify a project path and return the folder and project file.
 * @param path - The path to verify.
 * @returns The folder and project file, or null if verification failed.
 */
export async function verifyProjectPath(path: string): Promise<{ folder: string; file: string } | null> {
  if (!sdk || !path) {
    console.warn('[EditorActions] Failed to verify project path -', !sdk ? 'SDK not initialized' : 'path is empty');
    return null;
  }

  const pathExists = await sdk.os.exists(path);
  if (!pathExists) {
    console.warn('[EditorActions] Project path does not exist:', path);
    return null;
  }

  const result = await sdk.tools.getProjectFolderAndFile(path);
  console.log('[EditorActions] Verify project path result:', result);

  if (!result?.folder || !result?.file) {
    console.warn('[EditorActions]', !result ? 'Failed to get project folder and file' : `project ${!result.folder ? 'folder' : 'file'} not found`, path);
    return null;
  }

  return { folder: result.folder, file: result.file };
}

export async function checkPnpmAvailability(): Promise<PnpmCheckResult> {
  const checkPnpm = sdk?.tools?.checkPnpm;
  if (!checkPnpm) {
    return { available: true };
  }
  try {
    return await checkPnpm();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { available: false, error: message };
  }
}

export async function checkBlenderAvailability(
  optionalPath?: string,
  userSelected?: boolean,
): Promise<BlenderCheckResult> {
  const checkBlender = sdk?.tools?.checkBlender;
  if (!checkBlender) {
    return { available: true };
  }
  try {
    return await checkBlender(optionalPath, userSelected);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { available: false, error: message };
  }
}

export const OPEN_PROJECT_OVERLAY = 'openProject';
/**
 * Try to open a project from a given path.
 * @param path - The path to the project, either the project directory or a specific project file.
 * @returns True if the project was opened successfully, false otherwise.
 */
export async function tryOpenProject(path: string) {
  const openProjectStart = performance.now();
  console.log('[EditorActions] Try open project', path);
  if (!sdk) {
    console.warn('[EditorActions] Failed to open project - SDK not initialized');
    return false;
  }
  if (!path) {
    console.warn('[EditorActions] Failed to open project - path is empty');
    return false;
  }
  globalStore.clearBuildErrors();

  globalStore.showLoadingOverlay(OPEN_PROJECT_OVERLAY, {
    message: 'Opening Project...',
    useDimmedBackdrop: true,
    pinned: true,
  });
  let success = false;
  try {
    const verifyResult = await verifyProjectPath(path);
    if (!verifyResult) {
      toast.error(`Fail to open project - path not found: \n ${path}`, { duration: 10000 });
      console.warn('[EditorActions] Failed to open project - path not found', path);
      return false;
    }
    const { folder, file: projectFile } = verifyResult;
    console.log('[EditorActions] Get project folder and file from SDK:', { folder, projectFile });

    // Check if the directory and project file are not empty
    const directoryContents = await sdk.os.readDirectory(folder);
    if (!directoryContents || directoryContents.length === 0) {
      toast.error(`Fail to open project - folder is empty: \n ${path}`, { duration: 10000 });
      console.warn('[EditorActions] Failed to open project - folder is empty', path);
      return false;
    }
    if (!projectFile) {
      toast.error(`Fail to open project - no project file found: \n ${path}`, { duration: 10000 });
      console.warn('[EditorActions] Failed to open project - no project file found', path);
      return false;
    }

    const openResult = await sdk.tools.openProject(path);
    console.log('[EditorActions] Open project result:', openResult);
    if (!openResult.success) {
      globalStore.setBuildErrors([openResult.message ?? 'Failed to open project']);
      globalStore.setShowBuildErrorOverlay(true);
      return false;
    }

    globalStore.showLoadingOverlay(OPEN_PROJECT_OVERLAY, {
      message: 'Building Project...',
      useDimmedBackdrop: true,
      pinned: true,
    });
    const buildResult = await sdk.tools.buildProject(path);
    if (!buildResult.success) {
      globalStore.setBuildErrors([buildResult.message ?? 'Failed to build project']);
      globalStore.setShowBuildErrorOverlay(true);
      return false;
    }

    // Start file server
    globalStore.showLoadingOverlay(OPEN_PROJECT_OVERLAY, {
      message: 'Starting file server...',
      useDimmedBackdrop: true,
    });
    const fileServerPort = DEFAULT_FILE_SERVER_PORT;
    const error = await sdk.fileServer.start(fileServerPort, folder);
    if (error) {
      toast.error(`Fail to start file server: \n ${path} \n ${error.message}`, { duration: 10000 });
      console.error('[EditorActions] Failed to start file server', error);
      return false;
    } else {
      console.log('[EditorActions] File server started at port:', fileServerPort);
      const fileServerProvider = storageFactory.getProvider(StorageProviderType.FILE_SERVER) as FileServerProvider;
      const syncedBaseUrl = await syncFileServerProviderUrlFromSdk(fileServerProvider);
      if (syncedBaseUrl) {
        console.log('[EditorActions] Synced file server URL from SDK:', syncedBaseUrl);
      }
    }

    // Load project
    globalStore.showLoadingOverlay(OPEN_PROJECT_OVERLAY, {
      message: 'Loading Project...',
      useDimmedBackdrop: true,
    });
    const buildProject = false;
    await project.loadProject(folder, projectFile, buildProject);
    await sdk.app.setState('project.lastOpenedDirectory', path);

    // Pre-resolve renderer type so Canvas mounts with the correct one.
    try {
      const projectProvider = project.makeScopedStorageProvider();
      const [, projectSettings] = await ENGINE.loadProjectSettings(
        projectProvider,
        project.projectFilePathWithProjectPrefix
      );
      const rendererType = (projectSettings?.rendererType as ENGINE.RendererType) ?? DEFAULT_RENDERER_TYPE;
      sceneStore.setRendererType(rendererType);
    } catch (error) {
      console.warn('[EditorActions] Failed to pre-resolve renderer type:', error);
    }

    if (DEBUG_LOG.openProject) console.log('[EditorActions] 📂 Open project took:', ((performance.now() - openProjectStart) / 1000).toFixed(2), 's', buildProject ? '(with build)' : '(without build)');
    toast.info(`Project opened: \n ${project.projectPath}`);

    success = true;
    return true;
  } catch (error) {
    console.error('[EditorActions] Error opening project:', error);
    throw error;
  } finally {
    // Always dismiss the overlay on failure - on success it stays until canvas is ready
    if (!success) {
      globalStore.hideLoadingOverlay(OPEN_PROJECT_OVERLAY);
    }
  }
}

/**
 * Open a project requested from the host OS (for example file association / double-click).
 * Handles save/teardown when replacing an already-open project in the editor.
 */
export async function handleExternalProjectOpen(path: string): Promise<ExternalProjectOpenResult> {
  if (!sdk) {
    return { success: false, errorMessage: 'SDK is not initialised.' };
  }
  if (!path) {
    return { success: false, errorMessage: 'Project path is empty.' };
  }

  const verifyResult = await verifyProjectPath(path);
  if (!verifyResult) {
    return { success: false, errorMessage: `Project path is invalid or missing: ${path}` };
  }

  const targetProjectKey = normalizeProjectPathForCompare(verifyResult.folder, verifyResult.file);
  const currentProjectKey = normalizeProjectPathForCompare(project.projectFolder, project.projectFile);
  if (project.isProjectOpened && currentProjectKey === targetProjectKey) {
    console.log('[EditorActions] External project open ignored: requested project already open.');
    return { success: true };
  }

  const inEditorRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/editor');
  const shouldSaveCurrentProject = inEditorRoute && Boolean(store.project.currentScene) && project.isProjectOpened;

  const ioIdleBeforeSave = await waitForProjectIoIdle();
  if (!ioIdleBeforeSave) {
    return { success: false, errorMessage: 'Current project is busy (save/build). Please wait and try again.' };
  }

  if (shouldSaveCurrentProject) {
    const saveSuccess = await editorSave(true, true);
    if (!saveSuccess) {
      return { success: false, errorMessage: 'Failed to save current project before switching projects.' };
    }
  }

  const ioIdleBeforeSwitch = await waitForProjectIoIdle();
  if (!ioIdleBeforeSwitch) {
    return { success: false, errorMessage: 'Current project is still busy after save/build. Please wait and try again.' };
  }

  await closeProject();

  try {
    const openSuccess = await tryOpenProject(path);
    if (!openSuccess) {
      return { success: false, errorMessage: `Failed to open requested project: ${path}` };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { success: false, errorMessage: `Failed to open requested project: ${message}` };
  }

  return { success: true };
}

/**
 * Create a new project.
 * @param parentPath - The path to the parent directory.
 * @param projectName - The name of the project.
 * @param templateId - The ID of the template to use.
 * @returns True if the project was created and opened successfully, false otherwise.
 */
export async function createProject(parentPath: string, projectName: string, templateId: string): Promise<boolean> {
  console.log('ℹ️ Try Create project', parentPath, projectName, templateId);
  globalStore.showLoadingOverlay(OPEN_PROJECT_OVERLAY, {
    message: 'Creating Project...',
    useDimmedBackdrop: true,
    pinned: true,
  });
  try {
    const result = await sdk?.tools.createProject(parentPath, projectName, templateId);
    if (result?.success) {
      return await tryOpenProject(result.path);
    }
    globalStore.hideLoadingOverlay(OPEN_PROJECT_OVERLAY);
    toast.error(`Fail to create project: \n ${result?.message}`, { duration: 10000 });
    console.warn('❌ Failed to create project', projectName, parentPath, templateId, result);
    return false;
  } catch (error) {
    globalStore.hideLoadingOverlay(OPEN_PROJECT_OVERLAY);
    throw error;
  }
}

/**
 * Closes the currently open project: exits play mode, stops I/O services, and clears
 * `isProjectOpened` while retaining `projectFolder` / `projectFile` for reopen.
 */
export async function closeProject(): Promise<void> {
  if (!project.isProjectOpened) {
    return;
  }

  console.log('[EditorActions.closeProject] Closing project:', project.projectFolder);

  if (sceneStore.isPlayMode || sceneStore.isPlayModeStarting) {
    try {
      await setPlayModeActive(false);
    } catch (error) {
      console.warn('[EditorActions.closeProject] Failed to exit play mode:', error);
    }
  }

  try {
    await store.cleanupThumbnailService();
  } catch (error) {
    console.warn('[EditorActions.closeProject] Failed to cleanup thumbnail service:', error);
  }

  try {
    const status = await sdk?.fileServer?.status();
    if (status?.isRunning) {
      const stopError = await sdk?.fileServer?.stop();
      if (stopError) {
        console.warn('[EditorActions.closeProject] Failed to stop file server:', stopError);
      }
    }
  } catch (error) {
    console.warn('[EditorActions.closeProject] Failed to stop file server:', error);
  }

  project.markProjectClosed();
  store.undoRedoManager.clearAllStacks();
}

/**
 * Load the startup scene with loading overlay.
 * @param projectPath - The path to the project.
 */
export async function loadStartupScene(projectPath: string) {
  let openedProject = false;
  try {
    if (shouldOpenProjectBeforeStartup(project.isProjectOpened, projectPath)) {
      console.log('[EditorActions] Opening project from editor.');
      const success = await tryOpenProject(projectPath);
      if (!success) return;
      openedProject = true;
    } else if (!project.isProjectOpened) {
      console.warn('[EditorActions] Editor startup skipped project open — no project path.');
      return;
    }
    await store.loadStartupScene();
  } finally {
    // Also dismiss openProject overlay if we opened a project during this call
    if (openedProject) {
      globalStore.hideLoadingOverlay(OPEN_PROJECT_OVERLAY);
    }
  }
}

/**
 * Set play mode (PIE) active, building the project first if entering play mode.
 * @param active - Whether to enter or exit play mode.
 */
export async function setPlayModeActive(active: boolean, options: SetPlayModeActiveOptions = {}) {
  console.log('[EditorActions] 🎮 setPlayModeActive (PIE)', active);

  if (active) {
    globalStore.clearPlayModeErrors();
    const launchMode = options.launchMode ?? store.playLaunchMode;
    if (options.launchMode) {
      store.setPlayLaunchMode(options.launchMode);
    }

    // Check if we're in multiplayer mode
    const isMultiplayerMode = store.isMultiplayerMode;
    const serverLagSimulationConfig = createLagSimulationConfig(
      store.multiplayerServerLagEnabled,
      store.multiplayerServerLagLatencyMs,
      store.multiplayerServerLagJitterMs,
      store.multiplayerServerLagPacketLossPercent
    );
    const clientLagSimulationConfig = createLagSimulationConfig(
      store.multiplayerClientLagEnabled,
      store.multiplayerClientLagLatencyMs,
      store.multiplayerClientLagJitterMs,
      store.multiplayerClientLagPacketLossPercent
    );
    const playModeSceneLabel =
      store.playModeSceneType === PlayModeSceneType.CurrentScene ? 'Current Scene' : 'Game Default Scene';

    store.scene.setPlayModeStarting(true);
    globalStore.showLoadingOverlay('startPlayMode', {
      message: getPlayModeLoadingMessage(launchMode, playModeSceneLabel),
      pinned: true,
      useDimmedBackdrop: true,
    });

    let sdkPlaySessionStarted = false;
    try {
      const sceneReadyForSave = await waitForSceneReadyForSave();
      if (!sceneReadyForSave) {
        console.warn('[EditorActions] Scene did not finish loading in time, aborting play mode start.');
        toast.error('Scene is still loading. Please wait and try Play Mode again.');
        return;
      }

      const saveSucceeded = await editorSave(true, true);
      if (!saveSucceeded) {
        console.warn('[EditorActions] Save did not run successfully, aborting play mode start.');
        return;
      }

      const buildSuccess = await buildProject({ reloadWorldOnSuccess: false });
      if (!buildSuccess) {
        console.warn('[EditorActions] Build failed, not entering play mode.');
        return;
      }

      // Start multiplayer server if in multiplayer mode
      if (isMultiplayerMode) {
        try {
          globalStore.showLoadingOverlay('startPlayMode', {
            message: 'Starting Multiplayer Server...',
            pinned: true,
          });

          // Get the scene path to load on the server (uses defaultServerScene for GameDefault)
          const scenePath = store.playModeServerScenePath;
          console.log('[EditorActions] Starting server with scene:', scenePath);

          await multiplayerServerManager.startServer(project.projectDir, project.projectName, scenePath, serverLagSimulationConfig);
          console.log('[EditorActions] ✓ Multiplayer server started');
        } catch (error) {
          console.error('[EditorActions] Failed to start multiplayer server:', error);
          toast.error('Failed to start multiplayer server');
          return;
        }
      }

      // When running inside the Electron SDK, start the game in an isolated
      // WebContentsView so it cannot affect the editor renderer process.
      if (sdk && USE_ISOLATED_PLAY_MODE) {
        const storageProvider = project.makeScopedStorageProvider();
        const baseConfig = {
          scenePath: store.playModeScenePath,
          isMultiplayerMode,
          storageProviderType: storageProvider.providerType,
          projectPath: PROJECT_PATH_PREFIX,
          netSettings: {
            clientLagSimulationConfig,
          },
        };
        await Promise.race([
          (
            launchMode === PlayLaunchModeValues.Embedded
              ? sdk.playMode.start(baseConfig)
              : sdk.playMode.startWindow({
                ...baseConfig,
                window: makePlayWindowConfig(launchMode, store.playModeScenePath),
                emulation: launchMode === PlayLaunchModeValues.MobilePreview
                  ? makePlayMobilePreviewConfig()
                  : undefined,
              })
          ),
          new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Play mode start timed out')), PLAY_MODE_START_TIMEOUT_MS);
          }),
        ]);
        sdkPlaySessionStarted = true;
        store.scene.setPlayModeActive(true);

        // In multiplayer mode, open extra windows (one per additional client)
        // so the developer immediately has all chosen clients running side-by-side.
        if (isMultiplayerMode) {
          for (let i = 0; i < store.extraWindowCount; i++) {
            await sdk.playMode.startSecondaryWindow({
              ...baseConfig,
              isMultiplayerMode: true,
              window: makePlayWindowConfig(launchMode, store.playModeScenePath),
              emulation: launchMode === PlayLaunchModeValues.MobilePreview
                ? makePlayMobilePreviewConfig()
                : undefined,
            });
          }
        }
      } else {
        store.scene.setPlayModeActive(true);
      }
    } catch (error) {
      console.error('[EditorActions] Failed to enter play mode:', error);
      toast.error('Failed to start play mode');
      if (sdkPlaySessionStarted) {
        try {
          await stopSdkPlayModeResources();
        } catch (stopError) {
          console.error('[EditorActions] Failed to clean up SDK play mode after start failure:', stopError);
        }
        if (await multiplayerServerManager.isRunning()) {
          try {
            await multiplayerServerManager.stopServer();
          } catch (stopServerError) {
            console.error('[EditorActions] Failed to stop multiplayer server after play start failure:', stopServerError);
          }
        }
      }
      store.scene.setPlayModeActive(false);
    } finally {
      globalStore.hideLoadingOverlay('startPlayMode');
      store.scene.setPlayModeStarting(false);
      void drainQueuedExternalRefresh();
    }
    return;
  }

  try {
    // Stop multiplayer server when exiting play mode (if it's running)
    if (await multiplayerServerManager.isRunning()) {
      console.log('[EditorActions] Stopping multiplayer server...');
      try {
        await multiplayerServerManager.stopServer();
        console.log('[EditorActions] ✓ Multiplayer server stopped');
      } catch (error) {
        console.error('[EditorActions] Failed to stop multiplayer server:', error);
      }
    }

    // Destroy the isolated play view and secondary window when exiting play mode
    await stopSdkPlayModeResources();
  } finally {
    const suppressPlayModeOverlay = globalStore.consumeSuppressNextPlayModeOverlay();
    if (globalStore.hasPlayModeErrors && !suppressPlayModeOverlay) {
      globalStore.setShowPlayModeErrorOverlay(true);
    }
    store.scene.setPlayModeStarting(false);
    store.scene.setPlayModeActive(false);
    globalStore.hideLoadingOverlay('startPlayMode');
  }
}
